'use client';

/* eslint-disable max-len */
import { FC, memo, useEffect, useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as ISwiper } from 'swiper/types';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import classNames from 'classnames';
import { Container, IconSvg, Link, PrismicImageWithAlt } from '@/components';
import { useSelectedStore, useSliceItemsForSelectedStore } from '@/hooks';
import { isNullish } from '@/utils';
import { CarouselSlice } from '@/types';

import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/pagination';

import style from './hero.module.scss';

type HeroCarouselProps = CarouselSlice & {
  alternateItems?: CarouselSlice['items'];
  initialStoreUrlSlug?: string;
};

const HeroCarousel: FC<HeroCarouselProps> = ({
  alternateItems,
  initialStoreUrlSlug,
  ...slice
}) => {
  const { store } = useSelectedStore();
  const [isMounted, setIsMounted] = useState(false);
  const carouselDefault = slice?.variation === 'carouselSingleImagePerView';
  const carousel3Images = slice?.variation === 'carouselThreeImagesPerView';
  const carousel4Images = slice?.variation === 'carouselFourImagesPerView';
  const background = slice?.primary?.background_color ?? '';
  const fullWidth = slice?.primary?.full_width;
  const imgEffect = slice?.primary?.effect_zoom_images;
  const withousVerticalSpace = slice?.primary?.withous_vertical_space;
  const maxHeigthDesktop = carouselDefault
    ? slice?.primary?.max_height_desktop
    : 0;
  const maxHeigthMobile = carouselDefault
    ? slice?.primary?.max_height_mobile
    : 0;

  const backgroundImage = slice?.primary?.background_image?.url;
  const backgroundStyleFixed = slice?.primary?.background_style_fixed_cover;

  const carouselItems = useSliceItemsForSelectedStore({
    alternateItems,
    initialStoreUrlSlug,
    items: slice.items ?? [],
  });
  const numberOfItems = carouselItems.length;

  const [currentSlideIndex, setCurrentSlideIndex] = useState<number | null>(
    null,
  );

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const slideIndex = currentSlideIndex ?? 0;

  const isSlideVisible = (index: number) =>
    (!isMounted && index === 0) || (isMounted && !isNullish(currentSlideIndex));

  const currentBannerTop = useMemo(
    () => carouselItems[(slideIndex + 1) % numberOfItems],
    [slideIndex, carouselItems, numberOfItems],
  );

  const currentBannerBottom = useMemo(
    () => carouselItems[(slideIndex + 2) % numberOfItems],
    [slideIndex, carouselItems, numberOfItems],
  );

  const currentBannerThree = useMemo(
    () => carouselItems[(slideIndex + 3) % numberOfItems],
    [slideIndex, carouselItems, numberOfItems],
  );

  const handleSwiperInit = (swiper: ISwiper) => {
    setCurrentSlideIndex(swiper.realIndex);
  };

  const handleSlideChange = (swiper: ISwiper) => {
    setCurrentSlideIndex(swiper.realIndex);
  };

  const getImage = element => {
    if (carousel3Images) {
      return element?.background_image;
    }

    return element?.background_image?.mobile;
  };

  const renderImage = (banner, sizes, secondary, link, key) => {
    let image = <IconSvg name="image" />;

    if (banner?.url) {
      image = (
        <PrismicImageWithAlt
          defaultAlt="Hero image"
          fetchPriority="high"
          field={banner}
          fill
          height={400}
          loading="eager"
          priority={!secondary}
          quality={80}
          sizes={sizes}
          style={{ objectFit: 'cover', objectPosition: 'center' }}
          width={1000}
        />
      );
    }

    return (
      <Link
        aria-label={`Go to ${link ?? 'home'}`}
        className={classNames({ [style.banner__secondary]: secondary })}
        href={link ?? '/'}
        key={key}
      >
        <figure
          className={classNames({
            [style.banner__figure_simple]:
              (carouselDefault || carousel4Images) && !secondary,
            [style.banner__figure]: carousel3Images && !secondary,
            [style.banner__secondary_v]: carousel3Images && secondary,
            [style.banner__secondary_h]: carousel4Images && secondary,
          })}
        >
          {image}
        </figure>
        {!secondary && (
          <figure className={style.banner__figure_mobile}>
            {banner?.url ? (
              <PrismicImageWithAlt
                defaultAlt="Hero image"
                fetchPriority="high"
                field={banner?.mobile}
                fill
                height={250}
                loading="eager"
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                style={{ objectFit: 'cover', objectPosition: 'center center' }}
                width={400}
              />
            ) : (
              <IconSvg name="image" />
            )}
          </figure>
        )}
      </Link>
    );
  };

  if (!numberOfItems) {
    return null;
  }

  const sizes = '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33v';

  const sizesImageSecondary =
    '(min-width: 2048px) 688px, (min-width: 1536px) 512px, (min-width: 1280px) 430px, (min-width: 1024px) 358px, 0vw';

  return (
    <section
      className={classNames(style.hero, {
        [style.hero__withous_margin]: carouselDefault && withousVerticalSpace,
        [style.hero__with_margin]: !withousVerticalSpace,
        [style.hero__spacing_bottom]: slice?.primary?.bottom_spacing,
        [style.hero__background_image]: carousel4Images || carousel3Images,
        ['background__fixed_cover']: backgroundStyleFixed,
        ['background__contained']: !backgroundStyleFixed,
      })}
      key={`${slice?.variation}-${
        store?.url_slug ?? initialStoreUrlSlug ?? 'default'
      }`}
      style={{
        background,
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        ...(maxHeigthMobile && {
          '--carousel-max-height-mobile': `${maxHeigthMobile}px`,
        }),
        ...(maxHeigthDesktop && {
          '--carousel-max-height-desktop': `${maxHeigthDesktop}px`,
        }),
        ...(!fullWidth && {
          '--carousel-border-radius': `${slice?.primary?.border_radius ?? 0}px`,
        }),
      }}
    >
      <Container
        className={classNames(style.container__clear, {
          [style.container__grid]: !carouselDefault,
          [style.container__grid_tree_columns]: carousel4Images,
          [style.container__full_width]: fullWidth,
          [style.container__effect_img]: imgEffect,
          [style.container__grid_full]: fullWidth && !carouselDefault,
        })}
      >
        <div
          className={classNames({
            [style.banner__main_full]: carouselDefault,
            [style.banner__main]: carousel3Images,
            [style.banner__main_four_columns]: carousel4Images,
          })}
        >
          <Swiper
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
              stopOnLastSlide: false,
            }}
            className={classNames(style.swipper__hero_carousel, {
              swiper_pagination_container: carouselDefault,
            })}
            effect="fade"
            loop
            modules={[Autoplay, EffectFade, Navigation, Pagination]}
            onAfterInit={handleSwiperInit}
            onSlideChange={handleSlideChange}
            pagination={{
              clickable: true,
              renderBullet: (index, className) =>
                `<span class="${className}" role="button" tabindex="0" aria-label="Go to slide ${
                  index + 1
                }"></span>`,
            }}
            slidesPerView={1}
          >
            {carouselItems.map((banner, index) => (
              <SwiperSlide key={index}>
                {isSlideVisible(index) &&
                  renderImage(
                    banner.background_image,
                    sizes,
                    false,
                    banner.image_link,
                    `banner_main_${index}`,
                  )}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
        {!carouselDefault && (
          <>
            {carousel4Images &&
              renderImage(
                getImage(currentBannerThree),
                sizesImageSecondary,
                true,
                currentBannerThree?.image_link,
                `banner_three_${slideIndex}`,
              )}
            {renderImage(
              getImage(
                carousel3Images ? currentBannerTop : currentBannerBottom,
              ),
              sizesImageSecondary,
              true,
              currentBannerTop?.image_link,
              `banner_one_${slideIndex}`,
            )}
            {renderImage(
              getImage(
                carousel3Images ? currentBannerBottom : currentBannerTop,
              ),
              sizesImageSecondary,
              true,
              currentBannerBottom?.image_link,
              `banner_two_${slideIndex}`,
            )}
          </>
        )}
      </Container>
    </section>
  );
};

export default memo(HeroCarousel);
